/**
 * Crockford Base32：`0123456789ABCDEFGHJKMNPQRSTVWXYZ`
 *
 * 刻意去掉 `I` `L` `O` `U` 四个字符：`I` 与 `L` 易与 `1` 混，`O` 易与 `0` 混，
 * `U` 被排除以避免拼出脏词（Crockford 规范原意）。
 * 用途：地点码 `StockLocation.code`、商品码 `ProductCode.code`（各 8 位），
 * 以及码载荷里的类型位与校验位（见 `payload.ts`）。
 */
export const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const CODE_LENGTH = 8;
/** 2^32，用于把 32 位无符号随机数归一化到 [0, 1) */
const UINT32_RANGE = 0x1_0000_0000;
/**
 * 生成一个 8 位 Crockford Base32 码。
 *
 * @param random 取值源，返回 `[0, 1)` 的浮点数；默认用 Web Crypto 派生。
 *   注入是**为了可测**：用常量 0 / 0.999999 可断言边界输出。
 *
 * 32^8 = 1.1e12 种组合，对「一个社团的地点/商品数量级」而言碰撞概率可忽略；
 * 唯一性最终由数据库的 `@unique` 约束兜底（冲突时 `P2002`，由调用方重试或报错）。
 */
export function generateCode8(random) {
    const next = random ?? defaultRandom;
    let out = '';
    for (let i = 0; i < CODE_LENGTH; i += 1) {
        // **两端都要夹**：只夹上界时，注入的随机源返回负数会得到 `charAt(负)` → 空串，
        // `NaN` 同样产出空串。这些码会成为数据库唯一键与二维码载荷，**绝不能是空串或半截**。
        // 越界输入只可能来自测试夹具或调用方算错（默认随机源恒在 [0,1)），
        // 因此这里选择「静默夹取成合法码」而不是抛错：抛错会让调用方在运行期才发现，
        // 而夹取至少保证码合法、唯一性仍由数据库约束兜底。
        const raw = next();
        const unit = Number.isFinite(raw) ? Math.min(Math.max(raw, 0), 1) : 0;
        const idx = Math.min(Math.floor(unit * CROCKFORD_ALPHABET.length), CROCKFORD_ALPHABET.length - 1);
        out += CROCKFORD_ALPHABET.charAt(idx);
    }
    return out;
}
/**
 * 默认随机源：Web Crypto 而不是 `node:crypto`。
 *
 * 理由是这个包要同时被 Node（apps/api、CLI）与浏览器（apps/shop 的扫码解析）加载：
 * `node:crypto` 会让前端打包出现「模块已外部化」的告警，并在误调用时直接抛错。
 * `globalThis.crypto` 在 Node ≥ 22 与所有现代浏览器上都存在，且是同一套 CSPRNG 语义。
 */
function defaultRandom() {
    const buffer = new Uint32Array(1);
    globalThis.crypto.getRandomValues(buffer);
    return (buffer[0] ?? 0) / UINT32_RANGE;
}
//# sourceMappingURL=crockford.js.map